import React from 'react';
import { DomainScanResult } from '@/types';

interface SEOProps {
  // For search results
  results?: DomainScanResult[];
  searchedDomain?: string;
  // For static pages
  title?: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImage?: string;
  ogType?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterCard?: string;
  twitterImage?: string;
  noindex?: boolean;
  structuredData?: any;
  breadcrumbs?: Array<{
    name: string;
    url: string;
  }>;
}

const SEO: React.FC<SEOProps> = ({
  results = [],
  searchedDomain,
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  ogType = 'website',
  twitterTitle,
  twitterDescription,
  twitterCard = 'summary_large_image',
  twitterImage,
  noindex,
  structuredData,
  breadcrumbs
}) => {
  // Generate structured data for domain search results
  const generateDomainStructuredData = () => {
    if (!results.length) return null;

    const domainResults = results.map(result => ({
      "@type": "DomainSearchResult",
      "domain": result.domain,
      "status": result.status,
      "available": result.status === 'available',
      "pricing": result.pricing ? {
        "@type": "PriceSpecification",
        "price": result.pricing.purchasePrice,
        "priceCurrency": result.pricing.currency,
        "seller": result.pricing.registrar
      } : undefined,
      "valuation": result.valuation ? {
        "@type": "MonetaryAmount",
        "value": result.valuation.estimatedValue.max,
        "currency": "USD"
      } : undefined
    }));

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": `Domain Search Results for ${searchedDomain || 'Multiple Domains'}`,
      "description": `Search results showing availability, pricing, and valuation for ${results.length} domain${results.length > 1 ? 's' : ''}`,
      "numberOfItems": results.length,
      "itemListElement": domainResults
    };
  };

  // Generate breadcrumb structured data
  const generateBreadcrumbData = () => {
    if (breadcrumbs) {
      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": breadcrumb.name,
          "item": breadcrumb.url.startsWith('http') ? breadcrumb.url : `https://dombase.xyz${breadcrumb.url}`
        }))
      };
    }

    const defaultBreadcrumbs = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://dombase.xyz/"
      }
    ];

    if (searchedDomain) {
      defaultBreadcrumbs.push({
        "@type": "ListItem",
        "position": 2,
        "name": `Search: ${searchedDomain}`,
        "item": `https://dombase.xyz/?q=${encodeURIComponent(searchedDomain)}`
      });
    }

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": defaultBreadcrumbs
    };
  };

  // Generate page title and description based on results
  const getPageTitle = () => {
    if (title) return title;
    if (searchedDomain && results.length > 0) {
      const domainResult = results.find(r => r.domain === searchedDomain);
      if (domainResult) {
        return `DomBase - ${searchedDomain} Domain Details & Pricing`;
      }
      return `DomBase - Search Results for ${searchedDomain}`;
    }
    return "DomBase - Find Your Perfect Domain in Seconds, Not Days";
  };

  const getPageDescription = () => {
    if (description) return description;
    if (searchedDomain && results.length > 0) {
      const domainResult = results.find(r => r.domain === searchedDomain);
      if (domainResult) {
        return `Get instant availability, WHOIS data, pricing from Name.com, and valuation for ${searchedDomain}. Status: ${domainResult.status}.`;
      }
      return `Explore availability and pricing for ${searchedDomain} and its variants.`;
    }
    return "Find your perfect domain with instant pricing, availability, and WHOIS data. Compare domain costs across registrars, get valuations, and discover premium domains. Free domain checker trusted by 10,000+ businesses.";
  };

  const getKeywords = () => {
    const baseKeywords = "domain checker, domain availability, whois lookup, dns lookup, domain scanner, free domain checker, domain search, available domains, domain registration, domain tools, bulk domain checker, domain finder, domain pricing, domain cost, domain price checker, cheap domains, premium domains, domain valuation, domain appraisal, name.com pricing, domain registrar prices";

    if (searchedDomain) {
      return `${searchedDomain}, ${searchedDomain} domain, domain pricing, domain valuation, whois ${searchedDomain}, buy ${searchedDomain}, ${baseKeywords}`;
    }

    return baseKeywords;
  };

  const getCanonicalUrl = () => {
    if (canonical) {
      return canonical.startsWith('http') ? canonical : `https://dombase.xyz${canonical}`;
    }
    return `https://dombase.xyz${window.location.pathname}${window.location.search}`;
  };

  const domainStructuredData = generateDomainStructuredData();
  const breadcrumbData = generateBreadcrumbData();

  return (
    <>
      <title>{getPageTitle()}</title>
      <meta name="description" content={getPageDescription()} />
      <meta name="keywords" content={getKeywords()} />
      <link rel="canonical" href={getCanonicalUrl()} />

      {noindex && <meta name="robots" content="noindex" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogTitle || getPageTitle()} />
      <meta property="og:description" content={ogDescription || getPageDescription()} />
      <meta property="og:url" content={ogUrl || getCanonicalUrl()} />
      <meta property="og:site_name" content="DomBase" />
      {ogImage && (
        <>
          <meta property="og:image" content={ogImage.startsWith('http') ? ogImage : `https://dombase.xyz${ogImage}`} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={ogTitle || getPageTitle()} />
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle || getPageTitle()} />
      <meta name="twitter:description" content={twitterDescription || getPageDescription()} />
      {(twitterImage || ogImage) && (
        <meta name="twitter:image" content={(twitterImage || ogImage)!.startsWith('http') ? (twitterImage || ogImage)! : `https://dombase.xyz${twitterImage || ogImage}`} />
      )}

      {/* Structured Data */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((data, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )
      )}

      {domainStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(domainStructuredData)}
        </script>
      )}

      {breadcrumbData && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData)}
        </script>
      )}
    </>
  );
};

export default SEO;